import React from 'react'
import _ from 'lodash'
import { Colors, Icon, Popover, PopoverInteractionKind, Position } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import { EntityPreview } from 'browser/components/atomic-elements/organisms/entity/entity-preview/entity-preview'
import ComponentsMap from 'browser/components'
import apis from 'browser/app/models/apis'
import { PromiseItem } from '../../promise-item'

interface IEntityPreviewInlineProps {
  entityId?: string
  entity?: any
  text?: string
  previewSchema?: string
  className?: string
}

export const EntityPreviewInline: React.FC<IEntityPreviewInlineProps> = (props) => {
  const { entity, entityId: entityIdProp, text, previewSchema, className } = props

  const resolvedId = entity?.entityId || entityIdProp
  const hasDisplayName = !_.isEmpty(entity?.displayName)
  const shouldFetch = Boolean(resolvedId && !hasDisplayName)
  const promise = shouldFetch
    ? apis.getStore().getOrFetchRecord(resolvedId!)
    : Promise.resolve(entity ?? undefined)

  return (
    <PromiseItem
      promise={promise}
      mapper={(resolved: any) => {
        const full = resolved || entity
        if (!resolvedId && !full) {
          return <span className={className}>{text}</span>
        }

        const label = resolveDisplayName(full, resolvedId, text)
        const entityId = resolvedId || full?.entityId
        if (!entityId) {
          return <span className={className}>{label}</span>
        }
        const edgeValue = {
          entityId,
          displayName: label,
          ...(full?.denormalizedProperties
            ? { denormalizedProperties: full.denormalizedProperties }
            : {}),
        }

        const preview = (
          <EntityPreview
            key={entityId}
            value={edgeValue}
            components={ComponentsMap}
            previewSchema={previewSchema || 'uiSchema.web.entityPreviewPopover'}
          />
        )

        return (
          <Popover
            content={<div className="c-entityPreviewPopover">{preview}</div>}
            interactionKind={PopoverInteractionKind.HOVER}
            minimal
            position={Position.BOTTOM_LEFT}
          >
            <span className={className}>
              <Icon
                icon={IconNames.INFO_SIGN}
                color={Colors.ORANGE5}
                className="pr1"
              />
              {label}
            </span>
          </Popover>
        )
      }}
    />
  )
}

function resolveDisplayName(entity: any, fallbackId?: string, fallbackText?: string): string {
  if (!entity) {
    return fallbackText || fallbackId || 'Unknown'
  }

  if (entity.displayName) {
    return entity.displayName
  }

  const denormalizedProperties = entity.denormalizedProperties
  if (denormalizedProperties && typeof denormalizedProperties === 'object') {
    const nameKey = Object.keys(denormalizedProperties).find((k) =>
      /(^|\.)(name|displayName)$/i.test(k),
    )
    if (nameKey && denormalizedProperties[nameKey]) {
      return String(denormalizedProperties[nameKey])
    }
  }

  return entity.name || entity.title || fallbackText || fallbackId || 'Unknown'
}
